import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { useOfflineSyncState } from "../lib/OfflineSyncContext";
import { LayoutDashboard, ShoppingCart, Package, Users, CalendarDays, Send, Gift, Settings as Cog, LogOut, Mountain, ClipboardList, Moon, Sun, WifiOff, Trash2, RefreshCw } from "lucide-react";

const ITEMS = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, tid: "nav-dashboard", end: true, perm: "dashboard" },
  { to: "/pos", label: "POS Kasir", icon: ShoppingCart, tid: "nav-pos", perm: "pos" },
  { to: "/bookings", label: "Booking", icon: ClipboardList, tid: "nav-bookings", perm: "bookings" },
  { to: "/reservations", label: "Kalender", icon: CalendarDays, tid: "nav-reservations", perm: "calendar" },
  { to: "/inventory", label: "Inventaris", icon: Package, tid: "nav-inventory", perm: "inventory" },
  { to: "/customers", label: "Pelanggan", icon: Users, tid: "nav-customers", perm: "customers" },
  { to: "/referrals", label: "Member", icon: Gift, tid: "nav-referrals", perm: "members" },
  { to: "/offline-queue", label: "Antrian", icon: RefreshCw, tid: "nav-offline-queue", perm: "pos" },
  { to: "/audit-logs", label: "Audit", icon: ClipboardList, tid: "nav-audit", perm: "settings", ownerOnly: true },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const { settings, darkMode, toggleDark } = useTheme();
  const { online, pending, errors, syncing, sync } = useOfflineSyncState();
  const navigate = useNavigate();
  const perms = user?.permissions || [];
  const has = (p) => user?.role === "owner" || perms.includes(p);
  const items = ITEMS.filter(it => it.ownerOnly ? user?.role === "owner" : has(it.perm));

  const doLogout = () => { logout(); navigate("/login"); };

  return (
    <div className="min-h-screen bg-neutral-50 flex">
      {/* Sidebar */}
      <aside className="w-64 bg-white border-r border-neutral-200 hidden md:flex flex-col" data-testid="sidebar">
        <div className="p-6 border-b border-neutral-200 flex items-center gap-3">
          {settings?.logo_base64 ? (
            <img src={settings.logo_base64} alt="logo" className="w-9 h-9 rounded object-cover" />
          ) : (
            <div className="w-9 h-9 rounded flex items-center justify-center" style={{ background: "var(--brand-primary)" }}>
              <Mountain className="w-5 h-5 text-white" strokeWidth={2} />
            </div>
          )}
          <div>
            <div className="font-display font-bold text-neutral-900 leading-none">{settings?.business_name || "RentalPro"}</div>
            <div className="text-[10px] uppercase tracking-wider text-neutral-500 mt-1">Outdoor Rental POS</div>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {items.map((it) => (
            <NavLink key={it.to} to={it.to} end={it.end} data-testid={it.tid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                  isActive ? "text-white" : "text-neutral-700 hover:bg-neutral-100"
                }`
              }
              style={({ isActive }) => isActive ? { background: "var(--brand-primary)" } : {}}
            >
              <it.icon className="w-4 h-4" />
              {it.label}
            </NavLink>
          ))}
          {user?.role === "owner" && (
            <>
            <NavLink to="/trash" data-testid="nav-trash"
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${isActive ? "text-white" : "text-neutral-700 hover:bg-neutral-100"}`}
              style={({ isActive }) => isActive ? { background: "var(--brand-primary)" } : {}}
            >
              <Trash2 className="w-4 h-4" /> Sampah
            </NavLink>
            <NavLink to="/settings" data-testid="nav-settings"
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${isActive ? "text-white" : "text-neutral-700 hover:bg-neutral-100"}`}
              style={({ isActive }) => isActive ? { background: "var(--brand-primary)" } : {}}
            >
              <Cog className="w-4 h-4" /> Pengaturan
            </NavLink>
            </>
          )}
        </nav>
        <div className="p-3 border-t border-neutral-200">
          <button data-testid="dark-toggle" onClick={toggleDark}
            className="w-full flex items-center gap-2 px-3 py-2 mb-2 text-sm text-neutral-700 hover:bg-neutral-100 rounded-md">
            {darkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            {darkMode ? "Mode Terang" : "Mode Gelap"}
          </button>
          <div className="px-3 py-2">
            <div className="text-xs uppercase tracking-wider text-neutral-500">{user?.role}</div>
            <div className="text-sm font-medium text-neutral-900 truncate">{user?.name}</div>
            <div className="text-xs text-neutral-500 truncate">{user?.email}</div>
          </div>
          <button data-testid="logout-btn" onClick={doLogout}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-neutral-700 hover:bg-neutral-100 rounded-md">
            <LogOut className="w-4 h-4" /> Keluar
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 left-0 right-0 bg-white border-b border-neutral-200 z-40 px-4 py-3 flex items-center justify-between">
        <div className="font-display font-bold">{settings?.business_name || "RentalPro"}</div>
        <div className="flex items-center gap-3">
          <button data-testid="dark-toggle-mobile" onClick={toggleDark} className="text-sm text-neutral-600" aria-label="toggle dark">
            {darkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>
          <button data-testid="logout-btn-mobile" onClick={doLogout} className="text-sm text-neutral-600">Keluar</button>
        </div>
      </div>

      <main className="flex-1 md:ml-0 mt-14 md:mt-0">
        {!online && (
          <div data-testid="offline-banner" className="bg-amber-500 text-white text-xs font-semibold px-4 py-2 flex items-center gap-2 sticky top-0 z-30">
            <WifiOff className="w-3 h-3 shrink-0" /> Mode Offline · Anda melihat data cache.
            {pending > 0 && <span> Transaksi baru tersimpan & akan tersinkron otomatis saat online ({pending} menunggu).</span>}
            <button data-testid="offline-view-queue" onClick={() => navigate("/offline-queue")} className="underline shrink-0 ml-auto">Lihat antrian</button>
          </div>
        )}
        {online && pending > 0 && (
          <div data-testid="sync-banner" className="bg-blue-600 text-white text-xs font-semibold px-4 py-2 flex items-center justify-between gap-2 sticky top-0 z-30">
            <span className="flex items-center gap-2"><RefreshCw className={`w-3 h-3 ${syncing ? "animate-spin" : ""}`} /> {pending} transaksi offline menunggu sinkron</span>
            <span className="flex items-center gap-3 shrink-0">
              <button data-testid="view-queue-btn" onClick={() => navigate("/offline-queue")} className="underline">Lihat antrian</button>
              <button data-testid="sync-now-btn" onClick={() => sync(false)} disabled={syncing} className="underline disabled:opacity-60">{syncing ? "Menyinkron..." : "Sinkron sekarang"}</button>
            </span>
          </div>
        )}
        {errors > 0 && (
          <div data-testid="sync-error-banner" className="bg-red-600 text-white text-xs font-semibold px-4 py-2 flex items-center justify-between gap-2 sticky top-0 z-30">
            <span>{errors} transaksi gagal disinkron (mis. stok berubah). Silakan cek antrian.</span>
            <button data-testid="sync-retry-btn" onClick={() => navigate("/offline-queue")} className="underline shrink-0">Lihat antrian</button>
          </div>
        )}
        <div className="p-4 md:p-8 max-w-[1400px] mx-auto">
          <Outlet />
        </div>
        {/* Mobile bottom nav */}
        <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-neutral-200 grid z-40" style={{gridTemplateColumns: `repeat(${Math.min(items.length, 5)}, 1fr)`}}>
          {items.slice(0,5).map(it => (
            <NavLink key={it.to} to={it.to} end={it.end}
              className={({isActive}) => `flex flex-col items-center py-2 text-[10px] ${isActive ? "text-red-600" : "text-neutral-500"}`}>
              <it.icon className="w-5 h-5 mb-1" /> {it.label}
            </NavLink>
          ))}
        </nav>
        <div className="h-16 md:hidden" />
      </main>
    </div>
  );
}
